import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function formatUpdatedAt(iso, now = new Date()) {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime()))
        return iso;
    const diffMs = now.getTime() - then.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 1)
        return 'just now';
    if (diffMin < 60)
        return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24)
        return `Updated ${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30)
        return `Updated ${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    return `Updated ${then.toISOString().slice(0, 10)}`;
}
function isFresh(iso, now = new Date()) {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime()))
        return false;
    return now.getTime() - then.getTime() < 24 * 60 * 60 * 1000;
}
function joinReviewers(r) {
    const list = Array.isArray(r) ? r : [r];
    if (list.length === 0)
        throw new Error('FreshnessStamp: reviewer array cannot be empty');
    return list.map((e) => e.displayName).join(', ');
}
export function FreshnessStamp(props) {
    const { updatedAt, source, reviewer, isSample, sourceHref, className } = props;
    // Contract C3 — hard guard. Runtime for dev; snapshot tests enforce at CI.
    if (updatedAt == null || updatedAt === '') {
        throw new Error('FreshnessStamp: `updatedAt` is required per brand-guide §6.5.2');
    }
    if (source == null || source === '') {
        throw new Error('FreshnessStamp: `source` is required per brand-guide §6.5.2');
    }
    if (reviewer == null || (Array.isArray(reviewer) && reviewer.length === 0)) {
        throw new Error('FreshnessStamp: `reviewer` is required per brand-guide §6.5.2');
    }
    const fresh = !isSample && isFresh(updatedAt);
    const timestampText = isSample ? 'SAMPLE' : formatUpdatedAt(updatedAt);
    const reviewerText = joinReviewers(reviewer);
    return (_jsxs("span", { className: className, "data-freshness-stamp": true, "data-sample": isSample ? 'true' : undefined, role: "status", "aria-label": `${timestampText}. Source: ${source}. Reviewed by ${reviewerText}.`, style: {
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            maxWidth: '100%',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-dim)',
            lineHeight: 'var(--line-height-body)',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
        }, children: [_jsxs("span", { "data-part": "lead", style: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }, children: [_jsx("span", { "aria-hidden": "true", "data-fresh": fresh ? 'true' : undefined, "data-sample-badge": isSample ? 'true' : undefined, style: {
                            display: 'inline-block',
                            width: '0.5rem',
                            height: '0.5rem',
                            borderRadius: '9999px',
                            background: isSample ? 'transparent' : 'var(--accent)',
                            border: isSample ? '1px solid var(--border-strong)' : 'none',
                            animation: fresh ? 'freshness-pulse var(--motion-duration-freshness) ease-in-out infinite' : 'none',
                        } }), _jsx("span", { "data-part": "timestamp", style: { minWidth: 0 }, children: timestampText })] }), _jsx("span", { "aria-hidden": "true", "data-part": "sep", children: "\u00B7" }), _jsxs("span", { "data-part": "source", style: { minWidth: 0 }, children: ["Source:", ' ', sourceHref ? (_jsx("a", { href: sourceHref, style: { color: 'inherit', textDecoration: 'underline' }, children: source })) : (source)] }), _jsx("span", { "aria-hidden": "true", "data-part": "sep", children: "\u00B7" }), _jsxs("span", { "data-part": "reviewer", style: { minWidth: 0 }, children: ["Reviewed by ", reviewerText] }), _jsx("style", { children: `
        @media (max-width: 560px) {
          [data-freshness-stamp] {
            display: flex !important;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }
          [data-freshness-stamp] [data-part="sep"] { display: none; }
        }
      ` })] }));
}
/**
 * Helper for print rendering: returns the plaintext form for `.print-footer`
 * fallbacks and CI diff assertions (Contract C9).
 */
export function freshnessStampText(props) {
    return [
        props.isSample ? 'SAMPLE' : formatUpdatedAt(props.updatedAt),
        `Source: ${props.source}`,
        `Reviewed by ${joinReviewers(props.reviewer)}`,
    ].join(' · ');
}
//# sourceMappingURL=FreshnessStamp.js.map